import { DEFAULT_GAME_SPEED, PLAYER_COLORS, SIM_VERSION, defaultConfig, type GameConfig } from '../engine/simulation/simulation';

/** First colour no slot uses yet. */
function freeColour(slots: LobbySlot[], except?: LobbySlot): number {
  return PLAYER_COLORS.find((c) => !slots.some((s) => s !== except && s.color === c)) ?? PLAYER_COLORS[0];
}
import type { Link } from './link';
import { PROTOCOL_VERSION, isNetMessage, type LobbySettings, type LobbySlot, type LobbyState, type NetMessage } from './protocol';

export const MAX_HUMANS = 4;

/**
 * A network session: the set of peer links plus the lobby state machine.
 * In mesh mode every peer is linked to every other. In relay (star) mode,
 * used for manual signalling, the host forwards messages between joiners.
 * The lobby host is the lowest-sorted connected peer id among the original
 * host and its successors, so the lobby survives the host leaving (migration).
 */
export class NetSession {
  readonly links = new Map<string, Link>();
  lobby: LobbyState;
  /** Message handlers for in-game traffic (set by the lockstep transport). */
  onGameMessage: ((m: NetMessage) => void) | null = null;
  onLobby: ((s: LobbyState) => void) | null = null;
  onStart: ((cfg: GameConfig, players: (string | null)[], inputDelay: number) => void) | null = null;
  onPeerLost: ((id: string) => void) | null = null;
  onLog: ((s: string) => void) | null = null;
  started = false;
  private seen = new Set<string>();
  /** Every peer id ever seen in this session, in join order. */
  members: string[] = [];

  constructor(readonly localId: string, public localName: string, isHost: boolean, readonly relay: boolean) {
    this.members.push(localId);
    this.lobby = {
      hostId: isHost ? localId : '',
      slots: isHost ? [{ peerId: localId, name: localName, civ: 'english', team: -1, color: PLAYER_COLORS[0], ready: true, ai: null }] : [],
      settings: { mapType: 'continental', mapSize: 'medium', seed: Math.floor(Math.random() * 1e6), startingAge: 0, gameSpeed: DEFAULT_GAME_SPEED, startingResources: 'standard', victory: ['conquest', 'wonder'], maxPlayers: 4 },
    };
  }

  get isHost(): boolean {
    return this.lobby.hostId === this.localId;
  }

  addLink(l: Link): void {
    this.links.set(l.remoteId, l);
    if (!this.members.includes(l.remoteId)) this.members.push(l.remoteId);
    l.onMessage = (m) => this.receive(l, m);
    l.onClose = () => this.lost(l.remoteId);
    l.send({ type: 'hello', from: this.localId, name: this.localName, protocol: PROTOCOL_VERSION, simVersion: SIM_VERSION });
    if (this.isHost) this.broadcastLobby();
  }

  /** Send to all peers (and, in relay mode, the host forwards). */
  broadcast(m: NetMessage): void {
    for (const l of this.links.values()) if (l.open) l.send(m);
  }

  private lost(id: string): void {
    this.links.delete(id);
    this.onLog?.(`Connection to ${id} lost.`);
    if (!this.started) {
      this.lobby.slots = this.lobby.slots.filter((s) => s.peerId !== id);
      if (this.lobby.hostId === id) this.migrateHost();
      else if (this.isHost) this.broadcastLobby();
      this.onLobby?.(this.lobby);
    }
    this.onPeerLost?.(id);
    // Relay hosts announce the loss to the joiners they serve.
    if (this.relay && this.isHost) this.broadcast({ type: 'leave', from: id });
  }

  private migrateHost(): void {
    const humans = this.lobby.slots.filter((s) => s.peerId).map((s) => s.peerId!);
    humans.sort();
    this.lobby.hostId = humans[0] ?? this.localId;
    this.onLog?.(`Lobby host is now ${this.lobby.hostId === this.localId ? 'you' : this.lobby.hostId}.`);
    if (this.isHost) this.broadcastLobby();
  }

  private receive(from: Link, raw: unknown): void {
    if (!isNetMessage(raw)) return;
    const m = raw;
    // Relay: the host forwards traffic between joiners that are not directly linked.
    if (this.relay && this.isHost && m.type !== 'hello' && m.type !== 'ping' && m.type !== 'pong') {
      const key = `${m.type}:${m.from}:${'tick' in m ? m.tick : ''}:${'player' in m ? m.player : ''}`;
      if (m.type === 'batch' || m.type === 'hash') {
        if (!this.seen.has(key)) {
          this.seen.add(key);
          for (const l of this.links.values()) if (l !== from && l.open) l.send(m);
        }
      } else if (m.type !== 'slot' && m.type !== 'resyncRequest') {
        for (const l of this.links.values()) if (l !== from && l.open) l.send(m);
      }
    }
    switch (m.type) {
      case 'hello':
        if (m.protocol !== PROTOCOL_VERSION || m.simVersion !== SIM_VERSION) {
          this.onLog?.(`${m.name} has an incompatible game version.`);
          from.close();
          return;
        }
        if (this.isHost && !this.started && !this.lobby.slots.some((s) => s.peerId === m.from)) {
          if (this.lobby.slots.filter((s) => s.peerId).length >= MAX_HUMANS || this.lobby.slots.length >= this.lobby.settings.maxPlayers) {
            this.onLog?.(`${m.name} could not join: lobby full.`);
            return;
          }
          this.lobby.slots.push({ peerId: m.from, name: m.name.slice(0, 24), civ: 'french', team: -1, color: freeColour(this.lobby.slots), ready: false, ai: null });
          this.broadcastLobby();
        }
        break;
      case 'lobby':
        if (m.from === this.lobby.hostId || !this.lobby.hostId || m.state.hostId === m.from) {
          this.lobby = m.state;
          this.onLobby?.(this.lobby);
        }
        break;
      case 'slot':
        if (this.isHost) {
          const s = this.lobby.slots.find((x) => x.peerId === m.from);
          if (s) {
            s.civ = m.civ;
            s.team = m.team;
            s.ready = m.ready;
            s.name = String(m.name).slice(0, 24) || s.name;
            // A colour someone else already has is refused; the player keeps their current one.
            if (PLAYER_COLORS.includes(m.color) && !this.lobby.slots.some((o) => o !== s && o.color === m.color)) s.color = m.color;
            this.broadcastLobby();
          }
        }
        break;
      case 'start':
        if (m.from === this.lobby.hostId && !this.started) {
          this.started = true;
          this.onStart?.(m.config, m.players, m.inputDelay);
        }
        break;
      case 'ping':
        from.send({ type: 'pong', from: this.localId, t: m.t });
        if (typeof m.fps === 'number' && typeof m.tick === 'number') this.peerPerf.set(m.from, { fps: m.fps, tick: m.tick, at: performance.now() });
        break;
      case 'pong':
        from.rtt = from.rtt * 0.8 + (performance.now() - m.t) * 0.2;
        break;
      case 'leave':
        if (m.from !== this.localId) this.onPeerLost?.(m.from);
        break;
      default:
        this.onGameMessage?.(m);
    }
  }

  // ---------- Lobby (host) ----------
  broadcastLobby(): void {
    if (!this.isHost) return;
    this.broadcast({ type: 'lobby', from: this.localId, state: this.lobby });
    this.onLobby?.(this.lobby);
  }

  updateSettings(s: Partial<LobbySettings>): void {
    if (!this.isHost) return;
    this.lobby.settings = { ...this.lobby.settings, ...s };
    this.broadcastLobby();
  }

  setSlot(index: number, s: Partial<LobbySlot>): void {
    if (!this.isHost) return;
    this.lobby.slots[index] = { ...this.lobby.slots[index], ...s };
    this.broadcastLobby();
  }

  addAI(): void {
    if (!this.isHost || this.lobby.slots.length >= this.lobby.settings.maxPlayers) return;
    this.lobby.slots.push({ peerId: null, name: `Computer ${this.lobby.slots.length}`, civ: 'mongols', team: -1, color: freeColour(this.lobby.slots), ready: true, ai: { difficulty: 'standard', personality: 'balanced' } });
    this.broadcastLobby();
  }

  removeSlot(i: number): void {
    if (!this.isHost || this.lobby.slots[i]?.peerId === this.localId) return;
    this.lobby.slots.splice(i, 1);
    this.broadcastLobby();
  }

  /** Update own slot: civilisation, team, readiness, and optionally name and colour. */
  sendSlot(civ: string, team: number, ready: boolean, name?: string, color?: number): void {
    const mine = this.lobby.slots.find((x) => x.peerId === this.localId);
    if (name !== undefined) this.localName = name.slice(0, 24);
    const col = color ?? mine?.color ?? PLAYER_COLORS[0];
    const m: NetMessage = { type: 'slot', from: this.localId, civ, team, ready, name: this.localName, color: col };
    if (this.isHost) {
      if (mine) {
        Object.assign(mine, { civ, team, ready, name: this.localName || mine.name });
        if (!this.lobby.slots.some((o) => o !== mine && o.color === col)) mine.color = col;
      }
      this.broadcastLobby();
    } else this.broadcast(m);
  }

  canStart(): string | null {
    const sl = this.lobby.slots;
    if (sl.length < 2) return 'Need at least two players.';
    if (sl.some((s) => s.peerId && !s.ready)) return 'Waiting for players to be ready.';
    if (sl.some((s) => s.peerId && s.peerId !== this.localId && !this.links.has(s.peerId) && !this.relay)) return 'Not all peers are connected yet.';
    return null;
  }

  /** Host: start the match. */
  start(): void {
    if (!this.isHost || this.canStart()) return;
    const st = this.lobby.settings;
    const cfg = defaultConfig({
      seed: st.seed, mapType: st.mapType, mapSize: st.mapSize, startingAge: st.startingAge, gameSpeed: st.gameSpeed ?? DEFAULT_GAME_SPEED, startingResources: st.startingResources,
      players: this.lobby.slots.map((s, i) => ({ name: s.name, civ: s.civ, team: s.team, color: s.color ?? PLAYER_COLORS[i], ai: s.ai })),
    });
    cfg.victory.conditions = st.victory.length ? st.victory : ['conquest'];
    let maxRtt = 0;
    for (const l of this.links.values()) maxRtt = Math.max(maxRtt, l.rtt);
    // Input delay covers one-way latency (relay doubles it) plus a safety margin; a tick lasts
    // 50 ms of game time, which is shorter in real time at faster game speeds.
    const tickMs = 50 / (cfg.gameSpeed ?? 1);
    const inputDelay = Math.min(18, Math.max(3, Math.ceil(((this.relay ? maxRtt : maxRtt / 2) + 60) / tickMs)));
    const players = this.lobby.slots.map((s) => s.peerId);
    this.started = true;
    this.broadcast({ type: 'start', from: this.localId, config: cfg, players, inputDelay });
    this.onStart?.(cfg, players, inputDelay);
  }

  /** Our frame rate and game tick, sent with every ping (see peerPerf). */
  localPerf = { fps: 0, tick: 0 };
  /** Each peer's last reported frame rate and game tick, and when it arrived (performance.now()). */
  readonly peerPerf = new Map<string, { fps: number; tick: number; at: number }>();

  pingAll(): void {
    for (const l of this.links.values()) if (l.open) l.send({ type: 'ping', from: this.localId, t: performance.now(), fps: this.localPerf.fps, tick: this.localPerf.tick });
  }

  close(): void {
    this.broadcast({ type: 'leave', from: this.localId });
    for (const l of this.links.values()) l.close();
    this.links.clear();
  }
}
